import { ROUTE } from "../constants/routes";

export type ProductArea =
  | "auth"
  | "onboarding"
  | "home"
  | "events"
  | "activities"
  | "profile"
  | "terreiro"
  | "financial"
  | "inventory"
  | "ritual_baths"
  | "gamification"
  | "settings"
  | "subscriptions"
  | "qr"
  | "system"
  | "social";

export type PageRegistryEntry = {
  pageKey: string;
  routePattern: string;
  productArea: ProductArea;
  sourcePage: string;
};

const patternToRegex = (pattern: string): RegExp => {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const withParams = escaped.replace(/:[^/]+/g, "[^/]+");
  return new RegExp(`^${withParams}$`);
};

const entry = (
  pageKey: string,
  routePattern: string,
  productArea: ProductArea,
  sourcePage: string
): PageRegistryEntry => ({
  pageKey,
  routePattern,
  productArea,
  sourcePage,
});

/** Most specific patterns first. */
export const PAGE_REGISTRY: PageRegistryEntry[] = [
  entry(
    "event_template_create",
    "/events/template/:id/create",
    "events",
    "CreateFromTemplateEvent"
  ),
  entry(
    "dev_subscription_test",
    "/dev/subscription-test",
    "subscriptions",
    "SubscriptionTestPage"
  ),
  entry(
    "oauth_google_result",
    ROUTE.OAUTH_GOOGLE_RESULT,
    "auth",
    "GoogleOAuthResult"
  ),
  entry(
    "oauth_facebook_result",
    ROUTE.OAUTH_FACEBOOK_RESULT,
    "auth",
    "FacebookOAuthResult"
  ),
  entry("signup_type", ROUTE.SIGNUP_TYPE, "auth", "SignupTypeSelection"),
  entry(
    "onboarding_medium",
    ROUTE.ONBOARDING_MEDIUM,
    "onboarding",
    "OnboardingMedium"
  ),
  entry(
    "onboarding_terreiro",
    ROUTE.ONBOARDING_TERREIRO,
    "onboarding",
    "OnboardingTerreiro"
  ),
  entry(
    "attendance_checkin_entry",
    ROUTE.ATTENDANCE_CHECKIN_ENTRY,
    "qr",
    "AttendanceCheckinEntry"
  ),
  entry(
    "activity_use_model",
    ROUTE.ACTIVITY_USE_MODEL,
    "activities",
    "CreateFromModelActivity"
  ),
  entry("activity_edit", ROUTE.ACTIVITY_EDIT, "activities", "EditActivity"),
  entry(
    "ritual_use_model",
    ROUTE.RITUAL_USE_MODEL,
    "activities",
    "CreateFromModelActivity"
  ),
  entry("ritual_edit", ROUTE.RITUAL_EDIT, "activities", "EditActivity"),
  entry("event_edit", ROUTE.EVENT_EDIT, "events", "EditEvent"),
  entry(
    "event_tickets",
    ROUTE.EVENT_TICKETS,
    "events",
    "EventTicketingManagement"
  ),
  entry("ingressos_new", ROUTE.INGRESSOS_NEW, "events", "CreateIngresso"),
  entry(
    "public_terreiro",
    ROUTE.PUBLIC_TERREIRO,
    "terreiro",
    "PublicTerreiroAgenda"
  ),
  entry("public_event", ROUTE.PUBLIC_EVENT, "events", "PublicEventTicket"),
  entry("ticket_voucher", ROUTE.TICKET_VOUCHER, "events", "TicketVoucher"),
  entry(
    "product_update_detail",
    ROUTE.UPDATE_DETAIL,
    "home",
    "ProductUpdateDetail"
  ),
  entry("product_updates", ROUTE.UPDATES, "home", "ProductUpdates"),
  entry(
    "profile_medium_data",
    ROUTE.PROFILE_MEDIUM_DATA,
    "profile",
    "MediumManagement"
  ),
  entry("profile_medium", ROUTE.PROFILE_MEDIUM, "profile", "MediumHub"),
  entry(
    "profile_address_with_id",
    ROUTE.PROFILE_ADDRESS_WITH_ID,
    "profile",
    "AddressManagement"
  ),
  entry("profile_data", ROUTE.PROFILE_DATA, "profile", "ProfileManagement"),
  entry(
    "profile_contacts",
    ROUTE.PROFILE_CONTACTS,
    "profile",
    "ContactsManagement"
  ),
  entry(
    "profile_credentials",
    ROUTE.PROFILE_CREDENTIALS,
    "profile",
    "CredentialsManagement"
  ),
  entry(
    "profile_passkeys",
    ROUTE.PROFILE_PASSKEYS,
    "profile",
    "PasskeysManagement"
  ),
  entry(
    "profile_address",
    ROUTE.PROFILE_ADDRESS,
    "profile",
    "AddressManagement"
  ),
  entry(
    "spiritual_profile_new",
    ROUTE.SPIRITUAL_PROFILE_NEW,
    "profile",
    "CreateSpiritualProfile"
  ),
  entry(
    "spiritual_profile",
    ROUTE.SPIRITUAL_PROFILE,
    "profile",
    "SpiritualProfile"
  ),
  entry("mediums_new", ROUTE.MEDIUMS_NEW, "profile", "CreateMedium"),
  entry("mediums_list", ROUTE.MEDIUMS_LIST, "profile", "Mediums"),
  entry(
    "mediums_attendance",
    ROUTE.MEDIUMS_ATTENDANCE,
    "profile",
    "AttendanceInsights"
  ),
  entry("mediums_ranks", ROUTE.MEDIUMS_RANKS, "profile", "MediumsRanks"),
  entry("mediums_lines", ROUTE.MEDIUMS_LINES, "profile", "MediumsLines"),
  entry(
    "attendance_insights",
    ROUTE.ATTENDANCE_INSIGHTS,
    "profile",
    "AttendanceInsightsRedirect"
  ),
  entry("activities_new", ROUTE.ACTIVITIES_NEW, "activities", "CreateActivity"),
  entry("rituals_new", ROUTE.RITUALS_NEW, "activities", "CreateActivity"),
  entry("events_new", ROUTE.EVENTS_NEW, "events", "CreateEvent"),
  entry("inventory_new", ROUTE.INVENTORY_NEW, "inventory", "CreateInventory"),
  entry("inventory_edit", ROUTE.INVENTORY_EDIT, "inventory", "EditInventory"),
  entry("inventory_detail", ROUTE.INVENTORY_VIEW, "inventory", "InventoryView"),
  entry("terreiro_join", ROUTE.TERREIRO_JOIN, "terreiro", "JoinTerreiro"),
  entry(
    "terreiro_profile",
    ROUTE.TERREIRO_PROFILE,
    "terreiro",
    "TerreiroProfileManagement"
  ),
  entry(
    "terreiro_address",
    ROUTE.TERREIRO_ADDRESS,
    "terreiro",
    "AddressManagement"
  ),
  entry(
    "terreiro_contact",
    ROUTE.TERREIRO_CONTACT,
    "terreiro",
    "ContactsManagement"
  ),
  entry(
    "terreiro_medium_view",
    ROUTE.TERREIRO_MEDIUM_VIEW,
    "terreiro",
    "MediumProfileView"
  ),
  entry(
    "financial_new",
    ROUTE.FINANCIAL_NEW,
    "financial",
    "CreateFinancialAccount"
  ),
  entry(
    "financial_account",
    ROUTE.FINANCIAL_ACCOUNT,
    "financial",
    "EditFinancialAccount"
  ),
  entry(
    "financial_monthly_fee_edit",
    ROUTE.FINANCIAL_MONTHLY_FEE_EDIT,
    "financial",
    "MonthlyFeeManagement"
  ),
  entry(
    "financial_monthly_fee_payments",
    ROUTE.FINANCIAL_MONTHLY_FEE_PAYMENTS,
    "financial",
    "MonthlyFeePaymentsManagement"
  ),
  entry(
    "financial_income_edit",
    ROUTE.FINANCIAL_INCOME_EDIT,
    "financial",
    "EditIncome"
  ),
  entry(
    "financial_outcome_edit",
    ROUTE.FINANCIAL_OUTCOME_EDIT,
    "financial",
    "EditOutcome"
  ),
  entry(
    "financial_transparency",
    ROUTE.FINANCIAL_TRANSPARENCY,
    "financial",
    "FinancialTransparency"
  ),
  entry(
    "transactions_new",
    ROUTE.TRANSACTIONS_NEW,
    "financial",
    "CreateTransaction"
  ),
  entry(
    "settings_meetings",
    ROUTE.SETTINGS_MEETINGS,
    "settings",
    "SettingsMeetings"
  ),
  entry("settings_usage", ROUTE.SETTINGS_USAGE, "settings", "UsageQuota"),
  entry("settings_plan", ROUTE.SETTINGS_PLAN, "settings", "PlanManagement"),
  entry(
    "settings_notifications",
    ROUTE.SETTINGS_NOTIFICATIONS,
    "settings",
    "NotificationSettings"
  ),
  entry(
    "settings_privacy_policy",
    ROUTE.SETTINGS_PRIVACY_POLICY,
    "settings",
    "TerreiroPolicy"
  ),
  entry(
    "affiliate_program",
    ROUTE.AFFILIATE_PROGRAM,
    "settings",
    "AffiliateProgram"
  ),
  entry("role_create", ROUTE.ROLE_CREATE, "settings", "RoleCreate"),
  entry("role_edit", ROUTE.ROLE_EDIT, "settings", "RoleEdit"),
  entry("my_permissions", ROUTE.MY_PERMISSIONS, "settings", "MyPermissions"),
  entry("role_management", ROUTE.ROLE_MANAGEMENT, "settings", "RoleManagement"),
  entry(
    "subscription_required",
    ROUTE.SUBSCRIPTION_REQUIRED,
    "subscriptions",
    "SubscriptionRequiredForMedium"
  ),
  entry(
    "subscription_successful",
    ROUTE.SUBSCRIPTION_SUCCESSFUL,
    "subscriptions",
    "SubscriptionSuccessFull"
  ),
  entry(
    "subscription_cancelation",
    ROUTE.SUBSCRIPTION_CANCELATION,
    "subscriptions",
    "SubscriptionCancelation"
  ),
  entry(
    "ritual_baths_new",
    ROUTE.RITUAL_BATHS_NEW,
    "ritual_baths",
    "CreateRitualBath"
  ),
  entry(
    "ritual_bath_edit",
    ROUTE.RITUAL_BATH_EDIT,
    "ritual_baths",
    "EditRitualBath"
  ),
  entry(
    "ritual_bath_categories",
    ROUTE.RITUAL_BATH_CATEGORIES,
    "ritual_baths",
    "RitualBathCategories"
  ),
  entry(
    "gamification_help",
    ROUTE.GAMIFICATION_HELP,
    "gamification",
    "GamificationHelp"
  ),
  entry(
    "academy_module",
    ROUTE.ACADEMY_MODULE,
    "gamification",
    "AcademyModule"
  ),
  entry(
    "social_experience_new",
    ROUTE.SOCIAL_EXPERIENCES_NEW,
    "social",
    "CreateExperience"
  ),
  entry(
    "social_experience",
    ROUTE.SOCIAL_EXPERIENCE,
    "social",
    "ExperienceDetail"
  ),
  entry(
    "social_question_new",
    ROUTE.SOCIAL_QUESTION_NEW,
    "social",
    "CreateQuestion"
  ),
  entry("social_question", ROUTE.SOCIAL_QUESTION, "social", "QuestionDetail"),
  entry("social_questions", ROUTE.SOCIAL_QUESTIONS, "social", "Questions"),
  entry("social_similar", ROUTE.SOCIAL_SIMILAR, "social", "Similar"),
  entry(
    "social_profile_edit",
    ROUTE.SOCIAL_PROFILE_EDIT,
    "social",
    "EditJourney"
  ),
  entry(
    "social_profile_me",
    ROUTE.SOCIAL_PROFILE_ME,
    "social",
    "JourneyProfile"
  ),
  entry("social_profile", ROUTE.SOCIAL_PROFILE, "social", "JourneyProfile"),
  entry("social_discover", ROUTE.SOCIAL_DISCOVER, "social", "SocialDiscover"),
  entry("social_saved", ROUTE.SOCIAL_SAVED, "social", "SocialSaved"),
  entry(
    "social_connections",
    ROUTE.SOCIAL_CONNECTIONS,
    "social",
    "SocialConnections"
  ),
  entry(
    "social_connections_user",
    ROUTE.SOCIAL_CONNECTIONS_USER,
    "social",
    "SocialConnections"
  ),
  entry(
    "social_notifications",
    ROUTE.SOCIAL_NOTIFICATIONS,
    "social",
    "SocialNotifications"
  ),
  entry("social_privacy", ROUTE.SOCIAL_PRIVACY, "social", "SocialPrivacy"),
  entry(
    "social_conversation",
    ROUTE.SOCIAL_CONVERSATION,
    "social",
    "SocialConversation"
  ),
  entry("social_messages", ROUTE.SOCIAL_MESSAGES, "social", "SocialMessages"),
  entry(
    "social_onboarding",
    ROUTE.SOCIAL_ONBOARDING,
    "social",
    "SocialOnboarding"
  ),
  entry("social_home", ROUTE.SOCIAL, "social", "SocialHome"),
  entry("forgot_password", ROUTE.FORGOT_PASSWORD, "auth", "ForgotPassword"),
  entry("reset_password", ROUTE.RESET_PASSWORD, "auth", "ResetPassword"),
  entry("signin", ROUTE.SIGNIN, "auth", "SignIn"),
  entry("signup", ROUTE.SIGNUP, "auth", "SignUp"),
  entry("qr_return", ROUTE.QR_RETURN, "qr", "QrReturn"),
  entry("invite", ROUTE.INVITE, "qr", "InvitePage"),
  entry("activity_detail", ROUTE.ACTIVITY, "activities", "ActivityView"),
  entry("ritual_detail", ROUTE.RITUAL, "activities", "ActivityView"),
  entry("event_detail", ROUTE.EVENT, "events", "EventView"),
  entry("medium_detail", ROUTE.MEDIUM, "profile", "Profile"),
  entry(
    "ritual_bath_detail",
    ROUTE.RITUAL_BATH,
    "ritual_baths",
    "RitualBathView"
  ),
  entry("onboarding", ROUTE.ONBOARDING, "onboarding", "OnboardingPage"),
  entry("notifications", ROUTE.NOTIFICATIONS, "home", "Notifications"),
  entry("events_list", ROUTE.EVENTS, "events", "Events"),
  entry("activities_list", ROUTE.ACTIVITIES, "activities", "Activities"),
  entry("rituals_list", ROUTE.RITUALS, "activities", "Activities"),
  entry("profile", ROUTE.PROFILE, "profile", "Profile"),
  entry("mediums_panel", ROUTE.MEDIUMS, "profile", "MediumsPanel"),
  entry("inventory_list", ROUTE.INVENTORY, "inventory", "Inventory"),
  entry("terreiro", ROUTE.TERREIRO, "terreiro", "Terreiro"),
  entry(
    "terreiro_search",
    ROUTE.TERREIRO_SEARCH,
    "terreiro",
    "TerreiroSearch"
  ),
  entry(
    "financial_accounts",
    ROUTE.FINANCIAL,
    "financial",
    "FinancialAccounts"
  ),
  entry(
    "financial_income_list",
    ROUTE.FINANCIAL_INCOME,
    "financial",
    "IncomeList"
  ),
  entry(
    "financial_outcome_list",
    ROUTE.FINANCIAL_OUTCOME,
    "financial",
    "OutcomeList"
  ),
  entry("settings", ROUTE.SETTINGS, "settings", "Settings"),
  entry("ritual_baths_list", ROUTE.RITUAL_BATHS, "ritual_baths", "RitualBaths"),
  entry("leaderboard", ROUTE.LEADERBOARD, "gamification", "Leaderboard"),
  entry("badges", ROUTE.BADGES, "gamification", "Badges"),
  entry("academy", ROUTE.ACADEMY, "gamification", "Academy"),
  entry("qr_landing", ROUTE.QR, "qr", "QrLanding"),
  entry("moon_example", ROUTE.MOON_EXAMPLE, "system", "MoonDataExample"),
  entry("home", ROUTE.APP_HOME, "home", "Home"),
];

const PAGE_REGISTRY_WITH_REGEX = PAGE_REGISTRY.map((item) => ({
  ...item,
  regex: patternToRegex(item.routePattern),
}));

export const resolvePageKeyFromPathname = (
  pathname: string
): PageRegistryEntry | null => {
  const normalized = pathname.replace(/\/+$/, "") || "/";

  for (const item of PAGE_REGISTRY_WITH_REGEX) {
    if (item.regex.test(normalized)) {
      return {
        pageKey: item.pageKey,
        routePattern: item.routePattern,
        productArea: item.productArea,
        sourcePage: item.sourcePage,
      };
    }
  }

  return null;
};
